import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../../config/supabase';
import ProgressBar from './ProgressBar';
import SubtaskPanel from './SubtaskPanel';
import DependencyPanel from './DependencyPanel';

// ============================================================
// InlinePanel — the two-mode task detail panel.
// Mode 1 (view): read-only fields + subtask check/uncheck + dependency view.
// Mode 2 (edit): full field access.
// Direct port of the VWH-Task-GUI InlinePanel concept (SharePoint/MSAL),
// rewired to Supabase per Module_13_Task_Management_v1_02.md §05/§10.
// ============================================================

const STATUSES = ['Queue', 'In Progress', 'Blocked', 'Completed', 'Cancelled'];
const FLAGS = ['None', 'Blocked', 'On Hold'];
const PRIORITIES = ['Critical', 'Urgent', 'Important', 'Normal', 'Low'];
const RECURRENCE = ['none', 'daily', 'weekly', 'monthly'];
const ARCHIVE_OPTIONS = ['retain', 'archive', 'delete'];

function emptyForm(task) {
  return {
    task_name: task.task_name || '',
    workspace_tag: task.workspace_tag || 'General',
    status: task.status || 'Queue',
    flag: task.flag || 'None',
    priority: task.priority || 'Normal',
    phase: task.phase || '',
    bucket_name: task.bucket_name || '',
    start_date: task.start_date || '',
    due_date: task.due_date || '',
    notes: task.notes || '',
    progress_pct: task.progress_pct ?? 0,
    recurrence_rule: task.recurrence_rule || 'none',
    block_reason: task.block_reason || '',
    hold_reason: task.hold_reason || '',
    resume_date: task.resume_date || '',
    archive_option: task.archive_option || 'retain',
    archive_threshold_days: task.archive_threshold_days ?? 90,
  };
}

export default function InlinePanel({ task, allTasks = [], theme = 'light', onClose, onSaved, onDeleted, onShowToast, canEdit = true, canManage = true }) {
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [form, setForm] = useState(() => emptyForm(task));
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // Reset to a fresh copy of the task whenever it changes — but only while
    // in view mode, so a background refetch (e.g. a subtask toggle recalculating
    // progress_pct) never clobbers an in-progress edit.
    if (mode !== 'edit') {
      setForm(emptyForm(task));
    }
  }, [task, mode]);

  useEffect(() => {
    // Switching to a different task entirely always resets to view mode.
    setMode('view');
  }, [task.id]);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    const values = {
      ...form,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      resume_date: form.resume_date || null,
      progress_pct: Number(form.progress_pct) || 0,
      archive_threshold_days: Number(form.archive_threshold_days) || 90,
    };
    const originalForm = emptyForm(task);
    const updates = Object.fromEntries(Object.entries(values).filter(([key]) =>
      form[key] !== originalForm[key]));
    const { error } = await supabaseClient.from('tasks').update(updates).eq('id', task.id);
    setSaving(false);
    if (error) {
      alert('Error saving task: ' + error.message);
      return;
    }
    onShowToast && onShowToast('Task saved');
    setMode('view');
    onSaved && onSaved();
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${task.task_name}"? This writes an audit snapshot before removing it.`)) return;
    // Gate 2 pattern: snapshot before removal, same as the scheduled archive-delete sweep.
    const { error: archiveError } = await supabaseClient.from('task_archive_log').insert([{
      org_id: task.org_id,
      original_task_id: task.id,
      task_snapshot: task,
      export_reason: 'manual_delete',
    }]);
    if (archiveError) {
      alert('Error archiving task: ' + archiveError.message);
      return;
    }
    const { error } = await supabaseClient.from('tasks').delete().eq('id', task.id);
    if (error) {
      alert('Error deleting task: ' + error.message);
      return;
    }
    onShowToast && onShowToast('Task deleted');
    onDeleted && onDeleted();
  }

  function refetchAfterChildChange() {
    // Subtask/dependency changes update tasks.progress_pct / status server-side
    // (Postgres triggers) — bump a key so the list behind the panel refetches too.
    setRefreshKey(k => k + 1);
    onSaved && onSaved({ silent: true });
  }

  const editable = mode === 'edit';
  const panelBg = theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const labelClass = `text-xs font-medium uppercase tracking-wide mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`;
  const inputClass = `w-full text-sm rounded-md border px-3 py-1.5 ${
    theme === 'dark' ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
  }`;

  return (
    <ModalSurface closeOnEscape={false} onClose={onClose} label="Task details" overlayClassName="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30" onOverlayClick={onClose} panelClassName={`w-full max-w-xl h-full overflow-y-auto border-l shadow-xl ${panelBg}`} panelProps={{"data-m6-layout":"tasks","data-m6-component":"InlinePanel"}}>
        <div className={`sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b ${panelBg}`}>
          <div>
            {editable ? (
              <input
                value={form.task_name}
                onChange={(e) => set('task_name', e.target.value)}
                className="border-b border-[var(--surface-border)] bg-transparent text-lg font-bold text-[var(--ink)]"
              />
            ) : (
              <h2 className="text-lg font-bold text-[var(--ink)]">{task.task_name}</h2>
            )}
            <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{task.workspace_tag}</p>
          </div>
          <div className="flex items-center gap-2">
            {editable ? (
              <>
                <button onClick={() => { setForm(emptyForm(task)); setMode('view'); }} className="text-sm px-3 py-1.5 rounded-md border border-gray-400 text-gray-500 hover:bg-gray-100">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving} className="text-sm px-3 py-1.5 rounded-md bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              canEdit
                ? <button onClick={() => setMode('edit')} className="text-sm px-3 py-1.5 rounded-md bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)]">Edit</button>
                : null
            )}
            <button onClick={onClose} className="ia-modal-desktop-close px-2 text-xl leading-none text-[var(--ink-muted)] hover:text-[var(--ink)]">
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <ProgressBar progressPct={editable ? Number(form.progress_pct) : task.progress_pct} theme={theme} />
            {editable && (
              <input
                type="range" min="0" max="100" step="5"
                value={form.progress_pct}
                onChange={(e) => set('progress_pct', e.target.value)}
                className="w-full mt-2 accent-[var(--accent-ink)]"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className={labelClass}>Status</div>
              {editable ? (
                <select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputClass}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.status}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Flag</div>
              {editable ? (
                <select value={form.flag} onChange={(e) => set('flag', e.target.value)} className={inputClass}>
                  {FLAGS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.flag}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Priority</div>
              {editable ? (
                <select value={form.priority} onChange={(e) => set('priority', e.target.value)} className={inputClass}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.priority || '—'}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Bucket</div>
              {editable ? (
                <input value={form.bucket_name} onChange={(e) => set('bucket_name', e.target.value)} className={inputClass} />
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.bucket_name || '—'}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Phase</div>
              {editable ? (
                <input value={form.phase} onChange={(e) => set('phase', e.target.value)} className={inputClass} />
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.phase || '—'}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Quarter</div>
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{task.quarter || '—'} <em className="text-xs">(auto)</em></span>
            </div>
            <div>
              <div className={labelClass}>Start Date</div>
              {editable ? (
                <input type="date" value={form.start_date || ''} onChange={(e) => set('start_date', e.target.value)} className={inputClass} />
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.start_date || '—'}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Due Date</div>
              {editable ? (
                <input type="date" value={form.due_date || ''} onChange={(e) => set('due_date', e.target.value)} className={inputClass} />
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.due_date || '—'}</span>
              )}
            </div>
            <div>
              <div className={labelClass}>Recurrence</div>
              {editable ? (
                <select value={form.recurrence_rule} onChange={(e) => set('recurrence_rule', e.target.value)} className={inputClass}>
                  {RECURRENCE.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.recurrence_rule}</span>
              )}
            </div>
          </div>

          {(form.flag === 'Blocked' || task.status === 'Blocked') && (
            <div>
              <div className={labelClass}>Block Reason</div>
              {editable ? (
                <input value={form.block_reason} onChange={(e) => set('block_reason', e.target.value)} className={inputClass} />
              ) : (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.block_reason || '—'}</span>
              )}
            </div>
          )}

          {form.flag === 'On Hold' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className={labelClass}>Hold Reason</div>
                {editable ? (
                  <input value={form.hold_reason} onChange={(e) => set('hold_reason', e.target.value)} className={inputClass} />
                ) : (
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.hold_reason || '—'}</span>
                )}
              </div>
              <div>
                <div className={labelClass}>Resume Date</div>
                {editable ? (
                  <input type="date" value={form.resume_date || ''} onChange={(e) => set('resume_date', e.target.value)} className={inputClass} />
                ) : (
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.resume_date || '—'}</span>
                )}
              </div>
            </div>
          )}

          <div>
            <div className={labelClass}>Notes</div>
            {editable ? (
              <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} className={inputClass} />
            ) : (
              <p className={`text-sm whitespace-pre-wrap ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{task.notes || '—'}</p>
            )}
          </div>

          {editable && (
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-dashed border-gray-300">
              <div>
                <div className={labelClass}>Archive Option</div>
                <select value={form.archive_option} onChange={(e) => set('archive_option', e.target.value)} className={inputClass}>
                  {ARCHIVE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <div className={labelClass}>Archive Threshold (days)</div>
                <input type="number" min="7" value={form.archive_threshold_days} onChange={(e) => set('archive_threshold_days', e.target.value)} className={inputClass} />
              </div>
            </div>
          )}

          <SubtaskPanel key={`sub-${refreshKey}`} taskId={task.id} editable={editable && canEdit} theme={theme} onChanged={refetchAfterChildChange} />
          <DependencyPanel key={`dep-${refreshKey}`} taskId={task.id} allTasks={allTasks} editable={editable && canEdit} theme={theme} onChanged={refetchAfterChildChange} />

          {canManage && <div className="pt-4 border-t border-dashed border-gray-300 flex justify-end">
            <button onClick={handleDelete} className="text-sm text-red-600 hover:text-red-700 font-medium">
              Delete Task
            </button>
          </div>}
        </div>
      </ModalSurface>
  );
}
